import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getTariffs } from 'app/entities/tariff/tariff.reducer';
import { getEntities as getTaxTariffTranches } from 'app/entities/tax-tariff-tranche/tax-tariff-tranche.reducer';
import { getEntities as getGridOperatorCapacities } from 'app/entities/grid-operator-capacity/grid-operator-capacity.reducer';
import { getEntities as getProductOfferings } from 'app/entities/product-offering/product-offering.reducer';
import { createEntity, getEntity, reset, updateEntity } from './product-offering-price.reducer';

export const ProductOfferingPriceUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const tariffs = useAppSelector(state => state.tariff.entities);
  const taxTariffTranches = useAppSelector(state => state.taxTariffTranche.entities);
  const gridOperatorCapacities = useAppSelector(state => state.gridOperatorCapacity.entities);
  const productOfferings = useAppSelector(state => state.productOffering.entities);
  const productOfferingPriceEntity = useAppSelector(state => state.productOfferingPrice.entity);
  const loading = useAppSelector(state => state.productOfferingPrice.loading);
  const updating = useAppSelector(state => state.productOfferingPrice.updating);
  const updateSuccess = useAppSelector(state => state.productOfferingPrice.updateSuccess);

  const handleClose = () => {
    navigate('/product-offering-price');
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getTariffs({}));
    dispatch(getTaxTariffTranches({}));
    dispatch(getGridOperatorCapacities({}));
    dispatch(getProductOfferings({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }

    const entity = {
      ...productOfferingPriceEntity,
      ...values,
      tariffs: mapIdList(values.tariffs),
      taxTariffTranches: mapIdList(values.taxTariffTranches),
      gridOperatorCapacities: mapIdList(values.gridOperatorCapacities),
      productOffering: productOfferings.find(it => it.id.toString() === values.productOffering?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          ...productOfferingPriceEntity,
          tariffs: productOfferingPriceEntity?.tariffs?.map(e => e.id.toString()),
          taxTariffTranches: productOfferingPriceEntity?.taxTariffTranches?.map(e => e.id.toString()),
          gridOperatorCapacities: productOfferingPriceEntity?.gridOperatorCapacities?.map(e => e.id.toString()),
          productOffering: productOfferingPriceEntity?.productOffering?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2
            id="crawlerBackendApp.productOfferingPrice.home.createOrEditLabel"
            data-cy="ProductOfferingPriceCreateUpdateHeading"
          >
            <Translate contentKey="crawlerBackendApp.productOfferingPrice.home.createOrEditLabel">
              Create or edit a ProductOfferingPrice
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="product-offering-price-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('crawlerBackendApp.productOfferingPrice.effectiveDate')}
                id="product-offering-price-effectiveDate"
                name="effectiveDate"
                data-cy="effectiveDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productOfferingPrice.expirationDate')}
                id="product-offering-price-expirationDate"
                name="expirationDate"
                data-cy="expirationDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productOfferingPrice.description')}
                id="product-offering-price-description"
                name="description"
                data-cy="description"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.productOfferingPrice.tariff')}
                id="product-offering-price-tariff"
                data-cy="tariff"
                type="select"
                multiple
                name="tariffs"
              >
                <option value="" key="0" />
                {tariffs
                  ? tariffs.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.productOfferingPrice.taxTariffTranche')}
                id="product-offering-price-taxTariffTranche"
                data-cy="taxTariffTranche"
                type="select"
                multiple
                name="taxTariffTranches"
              >
                <option value="" key="0" />
                {taxTariffTranches
                  ? taxTariffTranches.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.productOfferingPrice.gridOperatorCapacity')}
                id="product-offering-price-gridOperatorCapacity"
                data-cy="gridOperatorCapacity"
                type="select"
                multiple
                name="gridOperatorCapacities"
              >
                <option value="" key="0" />
                {gridOperatorCapacities
                  ? gridOperatorCapacities.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="product-offering-price-productOffering"
                name="productOffering"
                data-cy="productOffering"
                label={translate('crawlerBackendApp.productOfferingPrice.productOffering')}
                type="select"
              >
                <option value="" key="0" />
                {productOfferings
                  ? productOfferings.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button
                tag={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/product-offering-price"
                replace
                color="info"
              >
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                type="submit"
                disabled={updating}
              >
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ProductOfferingPriceUpdate;
